import React from 'react';

import { makeStyles } from 'tss-react/mui';
import { units, NoSSR } from '@ha/dsl';

import { ListingCancellationPolicy } from '@ha/api/v2/types/Listing';
import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';

import { LazyComponent } from 'ha/components.legacy/LazyComponent';

import { LoadableSwiperSlider } from 'ha/components/SwiperSlider/SwiperSlide.lazy';

import { PhotoGalleryOverlay } from './PhotoGalleryOverlay';
import { PhotoGalleryOverlayActions } from './PhotoGalleryOverlayActions';

interface Props {
  room: NormalizedAlgoliaListing;
  isLazy?: boolean;
  scrollableElement?: HTMLElement | 'window';
  bottomOffset?: string;
  withGallery?: boolean;
  withFavorites?: boolean;
  // used in analytics
  cardLocation?: string;
}

const useStyles = makeStyles()(theme => ({
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    cursor: 'pointer',
  },
  imageContainerBorder: {
    borderRadius: 0,
    [theme.breakpoints.up('md')]: {
      borderRadius: units.rem(0.25),
    },
  },
  interactable: {
    zIndex: 2,
  },
  media: {
    position: 'relative',
    width: '100%',
    paddingTop: '66.66%' /* 3:2 Aspect Ratio */,
  },
  fullHeight: {
    height: '100%',
  },
}));

const MaxPhotoCount = 5;

const PhotoGalleryWithControlsSwiper: React.FC<Props> = ({
  room: { photos, photoCount, floorPlanCount, videoCount, cPolicy },
  room,
  isLazy,
  scrollableElement,
  bottomOffset,
  cardLocation,
  withFavorites,
}) => {
  const { classes, cx } = useStyles();

  const [index, setIndex] = React.useState(0);

  const slideCount = Math.min(photos?.length || 0, MaxPhotoCount);

  const slidePhotos = photos?.slice(0, slideCount);

  const handleChangeIndex = React.useCallback((newIndex: number) => {
    setIndex(newIndex);
  }, []);

  const isCancellationFlexible =
    cPolicy === ListingCancellationPolicy.FLEXIBLE_30D;
  const showMoreMediaSlide =
    index === slideCount - 1 && photoCount > MaxPhotoCount;

  const listingPhotos = (
    <LoadableSwiperSlider
      className={cx(classes.imageContainer, classes.imageContainerBorder)}
      urls={slidePhotos}
      onClickImage={handleChangeIndex}
      setIndexChange={handleChangeIndex}
    >
      {index === slideCount - 1 && photoCount > MaxPhotoCount && (
        <PhotoGalleryOverlay
          cardLocation={cardLocation}
          floorPlanCount={floorPlanCount}
          photoCount={photoCount}
          videoCount={videoCount}
        />
      )}
    </LoadableSwiperSlider>
  );

  return (
    <div className={classes.media}>
      <div
        className={cx(
          classes.absolute,
          classes.fullHeight,
          classes.interactable,
        )}
      >
        <NoSSR>
          {isLazy ? (
            <LazyComponent
              scrollableAncestor={scrollableElement}
              bottomOffset={bottomOffset}
            >
              {listingPhotos}
            </LazyComponent>
          ) : (
            listingPhotos
          )}
        </NoSSR>
      </div>
      <PhotoGalleryOverlayActions
        room={room}
        isCancellationFlexible={isCancellationFlexible}
        showMoreMediaSlide={showMoreMediaSlide}
        withFavorites={withFavorites}
      />
    </div>
  );
};

export { PhotoGalleryWithControlsSwiper };
